const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const finePointer = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

function initHero(hero) {
  if (hero.dataset.ready) return;
  hero.dataset.ready = 'true';
  if (reducedMotion() || window.Shopify?.designMode) return;

  const template = hero.querySelector('template[data-trail-source]');
  const trail = hero.querySelector('[data-trail]');
  if (!template || !trail) return;

  const sources = [...template.content.querySelectorAll('img')];
  if (!sources.length) return;
  const pool = [];

  let index = 0;
  const place = (x, y) => {
    const slot = index % sources.length;
    if (!pool[slot]) {
      const created = sources[slot].cloneNode(true);
      created.loading = 'eager';
      created.decoding = 'async';
      trail.append(created);
      pool[slot] = created;
    }
    const shot = pool[slot];
    index += 1;
    shot.classList.remove('is-on', 'is-off');
    shot.style.left = `${x}px`;
    shot.style.top = `${y}px`;
    shot.style.zIndex = String(index);
    const show = () => {
      hero.classList.add('is-trailing');
      requestAnimationFrame(() => requestAnimationFrame(() => shot.classList.add('is-on')));
      clearTimeout(shot.offTimer);
      shot.offTimer = setTimeout(() => shot.classList.add('is-off'), 900);
    };
    if (shot.complete && shot.naturalWidth) show();
    else shot.addEventListener('load', show, { once: true });
  };
  const spread = (step, box) => [box.width * (0.2 + 0.6 * ((step * 0.37) % 1)), box.height * (0.22 + 0.56 * ((step * 0.61) % 1))];

  if (finePointer()) {
    let lastX = -999;
    let lastY = -999;
    hero.addEventListener(
      'pointermove',
      (event) => {
        const box = hero.getBoundingClientRect();
        const x = event.clientX - box.left;
        const y = event.clientY - box.top;
        if (Math.hypot(x - lastX, y - lastY) < 110) return;
        lastX = x;
        lastY = y;
        place(x, y);
      },
      { passive: true }
    );
    return;
  }

  let visible = false;
  let step = 0;
  new IntersectionObserver(([entry]) => {
    visible = entry.isIntersecting;
  }).observe(hero);
  setInterval(() => {
    if (!visible || document.hidden) return;
    const box = hero.getBoundingClientRect();
    place(...spread(step, box));
    step += 1;
  }, 700);
}

class LucidAboutStack extends HTMLElement {
  #order = [];
  #cards = [];
  #busy = false;
  #startX = null;

  connectedCallback() {
    this.deck = this.querySelector('[data-deck]');
    this.count = this.querySelector('[data-stack-count]');
    this.#cards = [...this.querySelectorAll('[data-card]')];
    if (this.#cards.length < 2) return;
    this.#order = this.#cards.map((_, i) => i);

    this.querySelector('[data-next]')?.addEventListener('click', () => this.step(1));
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.step(-1));
    this.deck.addEventListener('click', () => this.step(1));
    this.deck.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.step(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.step(-1);
      }
    });
    this.deck.addEventListener('pointerdown', (event) => {
      this.#startX = event.clientX;
    });
    this.deck.addEventListener('pointerup', (event) => {
      if (this.#startX !== null && Math.abs(event.clientX - this.#startX) > 40) {
        this.#suppressClick();
        this.step(event.clientX < this.#startX ? 1 : -1);
      }
      this.#startX = null;
    });
  }

  #suppressClick() {
    this.deck.addEventListener('click', (event) => event.stopImmediatePropagation(), { capture: true, once: true });
  }

  #layout() {
    this.#order.forEach((cardIndex, position) => {
      const card = this.#cards[cardIndex];
      card.style.setProperty('--lc-k', String(position));
      if (position) card.setAttribute('aria-hidden', 'true');
      else card.removeAttribute('aria-hidden');
    });
    const format = this.dataset.statusFormat || '';
    if (this.count && format) {
      this.count.textContent = format.replace('[current]', String(this.#order[0] + 1)).replace('[total]', String(this.#cards.length));
    }
  }

  step(direction) {
    if (this.#busy) return;
    if (direction < 0 || reducedMotion()) {
      if (direction < 0) this.#order.unshift(this.#order.pop());
      else this.#order.push(this.#order.shift());
      this.#layout();
      return;
    }
    const top = this.#cards[this.#order[0]];
    this.#busy = true;
    top.classList.add('is-leaving');
    setTimeout(() => {
      top.classList.remove('is-leaving');
      this.#order.push(this.#order.shift());
      this.#layout();
      this.#busy = false;
    }, 380);
  }
}

if (!customElements.get('lucid-about-stack')) customElements.define('lucid-about-stack', LucidAboutStack);

const boot = (root = document) => root.querySelectorAll('[data-lucid-about-hero]').forEach(initHero);
boot();
document.addEventListener('shopify:section:load', (event) => boot(event.target));
